import { useMemo, useState } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { Result } from '@aicon-dev/caas-ui';
import { theme } from '@aicon-dev/caas-ui/dist/theme.css';
import { Pagination } from 'antd';
import clsx from 'clsx';
import styled from 'styled-components';

import { TablePure } from './Table.styled';
import {
  ExpandableColumnsType,
  TableBaseRow,
  TableClickRowHandler,
  TableColumnsType,
  TableProps,
} from './Table.types';

export const Table = <TRow extends TableBaseRow>(props: TableProps<TRow>) => {
  const {
    dataSource,
    columns,
    selectedRowKey,
    rowKey,
    size = 'medium',
    pagination,
    loading,
    style,
    onClickRow,
    emptyDescription,
    loadingDescription,
    footer,
    expandable,
  } = props;

  /**
   * 목록 갯수
   */
  const pageSize = useMemo(() => {
    return pagination?.limit ?? 10;
  }, [pagination?.limit]);

  /**
   * 불러온 데이터 중 첫번째의 DB상 순서
   */
  const parsedOffset = useMemo(() => {
    return pagination?.offset ?? 0;
  }, [pagination?.offset]);

  /**
   * 현재 페이지
   */
  const page = useMemo(() => {
    return parsedOffset / pageSize;
  }, [parsedOffset, pageSize]);

  const computedColumns = useMemo(() => columns.filter((e) => e !== null), [columns]);

  /**
   * Antd pagination handler
   *
   * @param page
   */
  const handleChangePage = (page: number) => {
    if (!pagination?.onChangePage) return;
    const parsedPage = page - 1;
    pagination.onChangePage(parsedPage * pageSize);
  };

  return (
    <>
      <TablePure className={clsx('material-table', `size-${size}`)} style={style}>
        <thead>
          <tr>
            {expandable ? <th style={{ width: '32px', padding: 0 }}></th> : null}
            {computedColumns.map((col, colIndex) => {
              return (
                <th
                  key={colIndex}
                  style={{
                    width: col?.width,
                  }}
                >
                  {col?.title || col?.dataIndex?.toString() || ''}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {dataSource?.map((row, rowIndex) => {
            const isSelected = selectedRowKey && rowKey ? selectedRowKey === row[rowKey] : false;

            return (
              <TableRow
                key={rowKey ? row[rowKey] : rowIndex}
                expandable={expandable}
                columns={columns}
                isSelected={isSelected}
                row={row}
                rowIndex={rowIndex}
                onClickRow={onClickRow}
              />
            );
          })}

          {dataSource && dataSource.length && footer ? (
            <tr style={{ borderTop: '2px solid #d9d9d9', ...footer.style }}>
              {expandable ? <td></td> : null}
              <td colSpan={footer.colSpan}>{footer.title}</td>
              <td>{footer.content}</td>
            </tr>
          ) : null}
        </tbody>
      </TablePure>
      {loading ? (
        <Box padding="100px 0px">
          <Result status="loading" title={loadingDescription || '로딩중입니다'} size="medium" />
        </Box>
      ) : dataSource && dataSource.length ? null : (
        <Box padding="100px 0px">
          <Result status="empty" title={emptyDescription || '데이터 없음'} size="medium" />
        </Box>
      )}

      {!!pagination && (
        <Box display="flex" justifyContent="center" py="1rem">
          <StyledPagination
            total={pagination.total}
            pageSize={pageSize}
            current={page + 1}
            showSizeChanger={false}
            onChange={handleChangePage}
          />
        </Box>
      )}
    </>
  );
};

interface TableRowProps<TRow = TableBaseRow>
  extends Omit<TableProps, 'columns' | 'rowkey' | 'onClickRow' | 'expandable'> {
  row: TRow;
  rowIndex: number;
  isSelected: boolean;
  columns: TableColumnsType<TRow>;
  onClickRow?: TableClickRowHandler<TRow>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expandable?: { expandableColumns?: ExpandableColumnsType<any> };
}

const TableRow = <TRow extends TableBaseRow>(props: TableRowProps<TRow>) => {
  const { columns, onClickRow, row, rowIndex, isSelected, expandable } = props;
  const [open, setOpen] = useState(false);

  const computedColumns = useMemo(() => columns.filter((e) => e !== null), [columns]);

  return (
    <>
      <tr
        className={clsx({
          'is-clickable': !!onClickRow,
          'is-selected': isSelected,
        })}
        onClick={(event) => {
          if (!onClickRow) return;
          onClickRow(row, rowIndex, event);
        }}
      >
        {expandable ? (
          <td
            style={{ textAlign: 'center', padding: 0, background: '#FFFFFF' }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M7 14L12 9L17 14"
                  stroke={theme.color.primary[500]}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M17 10L12 15L7 10"
                  stroke="#9E9E9E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </td>
        ) : null}
        {computedColumns.map((col, colIndex) => {
          const colValue = col?.dataIndex ? row[col?.dataIndex] : '';
          const colChildren = col?.render ? col.render(colValue, row, rowIndex) : colValue || '';

          return (
            <td
              key={colIndex}
              onClick={(event) => col?.onClick && col?.onClick(event, colValue, row, rowIndex)}
              style={{
                textAlign: col?.align,
                cursor: col?.onClick ? 'pointer' : undefined,
                ...col?.style,
              }}
            >
              {colChildren}
            </td>
          );
        })}
      </tr>
      {open
        ? row.children
          ? row.children.map((el: TRow['children'], childIndex: number) => {
              return (
                <tr key={childIndex} className="subTable">
                  <td></td>
                  {expandable?.expandableColumns?.map((item, idx) => {
                    return (
                      <td key={idx} style={{ ...item?.style }}>
                        {item?.render(el, childIndex)}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          : null
        : null}
    </>
  );
};

const StyledPagination = styled(Pagination)`
  .ant-pagination-item-link {
    border-radius: 4px;
    border-color: #eeeeee;
  }
  .ant-pagination-item {
    border-radius: 4px;
    color: #414c5c;
    font-weight: 400;
    font-size: var(--font-size-14, 14px);
    border-color: #eeeeee;

    &-active {
      border-color: ${theme.color.primary[500]};
    }
  }
`;
